import React, {
    useId,
    type HTMLInputTypeAttribute,
    type ReactElement,
} from "react";

import cx from "@hsl/core/utils/cx";

import Button, { type ButtonProps } from "../Button";

interface IconProps {
    icon: ReactElement<{
        className?: string;
        "aria-hidden"?: boolean;
        onClick?(): void;
    }>;
    position: "left" | "right";
}
export interface Props {
    buttonProps?: ButtonProps;
    containerClassNames?: string;
    className?: string;
    id?: string;
    label?: string;
    name: string;
    placeholder?: string;
    type?: HTMLInputTypeAttribute;
    value?: string | number;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    iconProps?: IconProps;
}

const Input = ({
    buttonProps,
    containerClassNames,
    className,
    id: idProp,
    label,
    name,
    placeholder,
    type,
    value,
    iconProps,
    onChange,
    onFocus,
}: Props) => {
    let id = useId();
    if (idProp) id = idProp;
    return (
        <div className={cx("px-px", containerClassNames)}>
            {!!label && (
                <label
                    htmlFor="email"
                    className="text-charcoal block text-sm font-medium leading-6"
                >
                    {label}
                </label>
            )}
            <div className="ring-platinum flex h-12 rounded-sm p-px shadow-sm ring-1 focus-within:ring-2 focus-within:ring-blue-300 md:h-[58px]">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                    {iconProps && (
                        <div
                            className={cx(
                                "absolute inset-y-0 flex items-center pl-3",
                                {
                                    "left-0":
                                        !iconProps.position ||
                                        iconProps.position === "left",
                                    "right-4": iconProps.position === "right",
                                },
                            )}
                        >
                            {React.cloneElement(iconProps.icon, {
                                className: cx(
                                    "h-5 w-5 text-warm-gray",
                                    iconProps.icon.props.className,
                                ),
                                "aria-hidden": true,
                            })}
                        </div>
                    )}
                    <input
                        type={type}
                        name={name}
                        id={id}
                        className={cx(
                            iconProps?.position === "right" ? "pl-4" : "pl-10",
                            "text-charcoal placeholder:text-taupe-grey block flex-1 rounded-sm border-none py-1.5 text-base placeholder:font-light placeholder:italic focus:border-0 focus:outline-0 focus-visible:outline-none sm:leading-6",
                            className,
                        )}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onFocus={onFocus}
                    />
                </div>
                {!!buttonProps && (
                    <Button
                        {...buttonProps}
                        className={cx(
                            "m-0.5 rounded-l-none rounded-r-sm text-base",
                            buttonProps.className,
                        )}
                    />
                )}
            </div>
        </div>
    );
};

Input.defaultProps = {
    type: "text",
    placeholder: "",
};

export default Input;
